import { useState } from "react";
import { Send, Car, Calendar, Wrench, User, FileText, Building2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { BRANDS, SERVICE_TYPES, buildOrderMessage, waLink } from "../lib/site";

const YEARS = Array.from({ length: 17 }, (_, i) => `${2026 - i}`);

const Field = ({ label, icon: Icon, error, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-2">
      <Icon size={15} className="text-red-500" />
      {label}
    </span>
    {children}
    {error && <span className="block mt-1.5 text-xs text-red-400">{error}</span>}
  </label>
);

const inputCls =
  "w-full bg-[#08090C]/70 border border-white/10 focus:border-red-500/60 focus:ring-1 focus:ring-red-500/30 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-all";

export const SensorFinder = ({ compact = false }) => {
  const [form, setForm] = useState({ name: "", make: "", model: "", year: "", service: "", details: "" });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "يرجى إدخال الاسم";
    if (!form.make) errs.make = "اختر الشركة المصنعة";
    if (!form.model.trim()) errs.model = "أدخل الموديل";
    if (!form.year) errs.year = "اختر سنة الصنع";
    if (!form.service) errs.service = "اختر نوع الخدمة";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    window.open(waLink(buildOrderMessage(form)), "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="finder"
      data-testid="sensor-finder-section"
      className={`relative py-20 sm:py-28 overflow-hidden ${compact ? "" : "bg-[#0E1118]"}`}
    >
      {!compact && (
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_50%_at_50%_100%,rgba(220,38,38,0.1),transparent)]" />
      )}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          align="center"
          eyebrow="Find Your Sensor"
          title="اعرف الحساس المناسب لسيارتك"
          desc="أدخل بيانات سيارتك وسنجهز رسالة واتساب تلقائية بكل التفاصيل — سنساعدك في تحديد الحساس المناسب بعد مراجعة بيانات السيارة."
        />
        <Reveal>
          <form
            onSubmit={submit}
            data-testid="finder-form"
            className="metal-card rounded-3xl p-6 sm:p-10 grid sm:grid-cols-2 gap-5"
            noValidate
          >
            <Field label="اسم العميل" icon={User} error={errors.name} testId="finder-field-name">
              <input
                type="text"
                data-testid="finder-input-name"
                className={inputCls}
                placeholder="مثال: أبو فهد"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="الشركة المصنعة" icon={Building2} error={errors.make} testId="finder-field-make">
              <select
                data-testid="finder-select-make"
                className={inputCls}
                value={form.make}
                onChange={(e) => set("make", e.target.value)}
              >
                <option value="">اختر الشركة</option>
                {BRANDS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
                <option value="أخرى">أخرى</option>
              </select>
            </Field>
            <Field label="الموديل" icon={Car} error={errors.model} testId="finder-field-model">
              <input
                type="text"
                data-testid="finder-input-model"
                className={inputCls}
                placeholder="مثال: كامري / لاندكروزر / باترول"
                value={form.model}
                onChange={(e) => set("model", e.target.value)}
              />
            </Field>
            <Field label="سنة الصنع" icon={Calendar} error={errors.year} testId="finder-field-year">
              <select
                data-testid="finder-select-year"
                className={inputCls}
                value={form.year}
                onChange={(e) => set("year", e.target.value)}
              >
                <option value="">اختر السنة</option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Field>
            <Field label="نوع الخدمة المطلوبة" icon={Wrench} error={errors.service} testId="finder-field-service">
              <select
                data-testid="finder-select-service"
                className={inputCls}
                value={form.service}
                onChange={(e) => set("service", e.target.value)}
              >
                <option value="">اختر الخدمة</option>
                {SERVICE_TYPES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="تفاصيل إضافية (اختياري)" icon={FileText} testId="finder-field-details">
              <input
                type="text"
                data-testid="finder-input-details"
                className={inputCls}
                placeholder="مثال: لمبة الحساس مضيئة بعد تبديل الكفرات"
                value={form.details}
                onChange={(e) => set("details", e.target.value)}
              />
            </Field>

            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="finder-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-7 py-4 rounded-2xl shadow-[0_4px_24px_rgba(16,185,129,0.4)] transition-all active:scale-[0.98] text-base"
              >
                <Send size={18} />
                إرسال الطلب عبر واتساب
              </button>
              <p className="mt-4 text-xs text-slate-500 text-center leading-relaxed">
                سيفتح واتساب برسالة جاهزة تحتوي بياناتك — لن يُرسَل أي شيء إلا
                بعد أن تضغط زر الإرسال داخل واتساب. لا نعرض أسعارًا أو نتائج
                توافق غير مؤكدة.
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
